import { apiClient } from './apiClient';

// Types
export interface ReconciliationDashboardData {
  summary: {
    totalPayments: number;
    matchedCount: number;
    unmatchedCount: number;
    discrepancyCount: number;
    resolvedCount: number;
    pendingResolutionCount: number;
  };
  recentRuns: Array<{
    id: string;
    startDate: string;
    endDate: string;
    runType: string;
    status: string;
    totalPayments: number;
    discrepancyCount: number;
    completedAt: string | null;
  }>;
  discrepanciesByType: Record<string, number>;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

export interface Discrepancy {
  id: string;
  paymentId: string;
  status: string;
  dbAmount: number;
  stripeAmount: number | null;
  discrepancyType: string | null;
  discrepancyAmount: number | null;
  discrepancyNotes: string | null;
  createdAt: string;
}

export interface ReconciliationRun {
  id: string;
  startDate: string;
  endDate: string;
  runType: string;
  totalPayments: number;
  matchedCount: number;
  unmatchedCount: number;
  discrepancyCount: number;
  totalAmount: number;
  status: string;
  startedAt: string;
  completedAt: string | null;
}

export interface RunReconciliationResult {
  runId: string;
  totalPayments: number;
  matchedCount: number;
  unmatchedCount: number;
  discrepancyCount: number;
  totalAmount: number;
  status: string;
  completedAt: string;
}

export interface DiscrepanciesResponse {
  discrepancies: Discrepancy[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface RunsResponse {
  runs: ReconciliationRun[];
}

export interface RunDetailsResponse {
  run: ReconciliationRun;
  records: Array<{
    id: string;
    paymentId: string;
    status: string;
    dbAmount: number;
    stripeAmount: number | null;
    discrepancyType: string | null;
    discrepancyAmount: number | null;
  }>;
}

const BASE = '/api/admin/reconciliation';

export const reconciliationApi = {
  /**
   * Get reconciliation dashboard summary
   */
  async getDashboard(startDate: string, endDate: string): Promise<ReconciliationDashboardData> {
    const response = await apiClient.get<{ success: boolean; data: ReconciliationDashboardData }>(
      `${BASE}/dashboard`,
      { params: { startDate, endDate } }
    );
    return response.data.data;
  },

  /**
   * Run a reconciliation process
   */
  async runReconciliation(
    startDate: string,
    endDate: string,
    runType: string = 'MANUAL'
  ): Promise<RunReconciliationResult> {
    const response = await apiClient.post<{ success: boolean; data: RunReconciliationResult }>(
      `${BASE}/run`,
      { startDate, endDate, runType }
    );
    return response.data.data;
  },

  /**
   * Get list of discrepancies with optional filters
   */
  async getDiscrepancies(params: {
    status?: string;
    discrepancyType?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<DiscrepanciesResponse> {
    const response = await apiClient.get<{ success: boolean; data: DiscrepanciesResponse }>(
      `${BASE}/discrepancies`,
      { params }
    );
    return response.data.data;
  },

  /**
   * Resolve a discrepancy
   */
  async resolveDiscrepancy(
    id: string,
    resolutionAction: string,
    resolutionNotes?: string
  ): Promise<void> {
    await apiClient.post(`${BASE}/${id}/resolve`, { resolutionAction, resolutionNotes });
  },

  /**
   * Get list of past reconciliation runs
   */
  async getRuns(limit?: number): Promise<RunsResponse> {
    const response = await apiClient.get<{ success: boolean; data: RunsResponse }>(
      `${BASE}/runs`,
      { params: { limit } }
    );
    return response.data.data;
  },

  /**
   * Get details of a specific reconciliation run
   */
  async getRunDetails(runId: string): Promise<RunDetailsResponse> {
    const response = await apiClient.get<{ success: boolean; data: RunDetailsResponse }>(
      `${BASE}/runs/${runId}`
    );
    return response.data.data;
  },

  /**
   * Export payments data as CSV or JSON
   */
  async exportPayments(
    startDate: string,
    endDate: string,
    format: 'csv' | 'json' = 'csv'
  ): Promise<Blob> {
    const response = await apiClient.get(`${BASE}/export`, {
      params: { startDate, endDate, format },
      responseType: 'blob',
    });
    return response.data;
  },
};
